import { CommonModule } from '@angular/common';
import { enableProdMode, importProvidersFrom, inject, LOCALE_ID, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { AkitaNgDevtools } from '@datorama/akita-ngdevtools';
import {
  DOCUMENT_INJECTION_TOKEN,
  getAppConfigProvider,
  IMPROVER_AVAILABLE_LANGUAGES,
  ImproverConstants,
  LOCATION_INJECTION_TOKEN,
  URL_INJECTION_TOKEN,
  WINDOW_INJECTION_TOKEN,
} from '@improver/app-config';
import { AuthInitializeModule } from '@improver/infrastructure/auth-data-layer';
import {
  AppInitService,
  getAppInitFactory,
  getCountryInitFactory,
  getEnvironmentInfo,
  getLoginInfoFactory,
  getSseConfigFactory,
  initializeLocale,
} from '@improver/infrastructure/initial-data-layer';
import { AppInitLocaleService, AVAILABLE_LANGUAGES, DAYJS_RELATIVE_TIME_CONFIG } from '@improver/infrastructure/intl-data-layer';
import { COUNTRY_PICKER_CONFIG } from '@improver/infrastructure/intl-shared';
import { BlockModule } from '@improver/shared-ui/block';
import { TranslateModule } from '@ngx-translate/core';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday.js';
import weekOfYear from 'dayjs/plugin/weekOfYear.js';
import { NgxPageScrollCoreModule } from 'ngx-page-scroll-core';
import { NgProgressModule } from 'ngx-progressbar';
import { AppComponent } from './app/app.component';
import { APP_TRANSLATE_CONFIG } from './app/app.constants';
import { AppRoutes } from './app/app.routes';
import { CoreModule } from './app/core/core.module';
import { environment } from './environments/environment';
import relativeTime from 'dayjs/plugin/relativeTime.js';

dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.extend(relativeTime, DAYJS_RELATIVE_TIME_CONFIG);

export function environmentFactory() {
  return environment;
}

const getCurrentLocale = (): string => {
  const defaultLocale = 'en-GB';

  if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
    return defaultLocale;
  }

  const navigator = window.navigator as any;
  const language = navigator.languages ? navigator.languages[0] : defaultLocale;

  return language || navigator.language || navigator.browserLanguage || navigator.userLanguage;
};

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      environment.production ? [] : AkitaNgDevtools.forRoot({ logTrace: false }),
      BlockModule.forRoot(),
      CommonModule,
      CoreModule,
      NgxPageScrollCoreModule.forRoot({
        duration: ImproverConstants.SCROLL_DEFAULT_DURATION,
      }),
      NgProgressModule.withConfig({
        thick: true,
        spinner: false,
        color: '#f05050',
      }),
      AuthInitializeModule.forRoot(),
      TranslateModule.forRoot(APP_TRANSLATE_CONFIG)
    ),
    AppInitService,
    AppInitLocaleService,
    provideAppInitializer(() => {
      const initializerFn = getLoginInfoFactory(inject(AppInitService));

      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = getSseConfigFactory(inject(AppInitService));

      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = getCountryInitFactory(inject(AppInitService));

      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = getAppInitFactory(inject(AppInitService));

      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeLocale(inject(AppInitService));

      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = getEnvironmentInfo(inject(AppInitService));

      return initializerFn();
    }),
    {
      provide: AVAILABLE_LANGUAGES,
      useValue: IMPROVER_AVAILABLE_LANGUAGES,
    },
    {
      provide: LOCALE_ID,
      useValue: getCurrentLocale(),
    },
    {
      provide: DOCUMENT_INJECTION_TOKEN,
      useValue: document,
    },
    {
      provide: WINDOW_INJECTION_TOKEN,
      useValue: window,
    },
    {
      provide: LOCATION_INJECTION_TOKEN,
      useValue: location,
    },
    {
      provide: URL_INJECTION_TOKEN,
      useValue: URL,
    },
    {
      provide: COUNTRY_PICKER_CONFIG,
      useValue: {
        defaultTranslations: {
          emptyLabel: 'FIELD_SELECT_OPTION_NONE',
          noResults: 'FIELD_SELECT_OPTION_NO_RESULTS',
        },
      },
    },
    getAppConfigProvider(environmentFactory),
    provideRouter(AppRoutes, withRouterConfig({ paramsInheritanceStrategy: 'always' }), withComponentInputBinding()),
  ],
});
